// The snaps a move can take, in the order they are tried: a
// product-specific rule (CO-5855) first, because it names the exact place
// that product belongs, then a corner (CO-5854), which pins it two ways, then
// a face met flush (CO-5853), which pins it one. Corners and faces are
// searched on the room's walls too (roomWalls.ts), by the same rules, and a
// face snap that meets or lines up with a wall is tried before a corner, so
// a product flush against a wall shows it. Callers take the first that
// leaves the product somewhere valid.
import * as THREE from 'three';
import {Axis} from 'components/customer/RoomPlanner/helpers/axes';
import {
    createSnapContext,
    SnapOptions,
} from 'components/customer/RoomPlanner/helpers/snapping/context';
import {findProductSnap} from 'components/customer/RoomPlanner/helpers/snapping/strategies/productSnap';
import {roomWallsOf} from 'components/customer/RoomPlanner/helpers/snapping/roomWalls';
import {
    SnapCandidate,
    SurfaceFacts,
} from 'components/customer/RoomPlanner/helpers/snapping/types';
import type {ExteriorFace} from 'components/customer/RoomPlanner/helpers/componentGeometry';
import {
    findFaceSnap,
    LinedUp,
    withWall,
} from 'components/customer/RoomPlanner/helpers/snapping/strategies/faceSnap';
import {
    findCornerSnap,
    HeldCorner,
} from 'components/customer/RoomPlanner/helpers/snapping/strategies/cornerSnap';
import {
    AlignmentBody,
    alignmentBodyOf,
    shiftBody,
} from 'components/customer/RoomPlanner/helpers/movement/faceAlignment';

/**
 * A snap a move can take: its position, how far it is from where the pointer
 * put the product, what offered it (a product rule's name, 'corner' or
 * 'face'), whether that was a product rule, the product it snapped to, and,
 * for a corner, what to hold next move, or, for a face, the face it met and
 * the planes it lined up with. The guides draw from these (CO-5858).
 */
export interface Snap extends SnapCandidate {
    route: string;
    rule: boolean;
    // The product or walls it snapped to.
    other?: SurfaceFacts;
    held?: HeldCorner;
    face?: ExteriorFace;
    // For a face, the ends it lined up with.
    aligned?: LinedUp[];
}

/**
 * What else a move tells the snaps.
 */
export interface SnapsOptions extends SnapOptions {
    // The axis the move is held to, or null for a free move.
    axis?: Axis | null;
    // The dragged product where the move puts it, when the caller has
    // measured it; otherwise measured here.
    body?: AlignmentBody;
    // The canvas size in pixels, which corners are judged on. Without it,
    // corners don't snap.
    viewport?: THREE.Vector2 | null;
    // The corner the drag held last move (CO-5854).
    heldCorner?: HeldCorner | null;
    // Which kinds of snap the top bar's choices allow (snapsAllowed,
    // CO-5856); each on when not given.
    rules?: boolean;
    faces?: boolean;
    corners?: boolean;
}

/**
 * The snaps for one move of a product: the nearest a product rule offers
 * across every other product, then, across the products no rule takes and
 * the room's walls, the corner snap on a free move and the face snap, the
 * face snap first when it meets or lines up with a wall, each only while
 * the top bar's choices allow it. Changes nothing.
 * @param {THREE.Group} product The dragged product.
 * @param {THREE.Vector3} target Where the move puts it.
 * @param {THREE.Group[]} products Every product in the room.
 * @param {SnapsOptions} options The camera, overlaps, walls, held axis and,
 * optionally, the caller's registry of product facts and the product's body.
 * @return {Snap[]} At most one product rule's snap, one corner snap and one
 * face snap, in that order, or the face snap before the corner snap when it
 * meets or lines up with a wall.
 */
export const findSnaps = (
    product: THREE.Group,
    target: THREE.Vector3,
    products: THREE.Group[],
    options: SnapsOptions
): Snap[] => {
    const ctx = createSnapContext(product, target, products, options);
    const {snap: rule, unclaimed} = findProductSnap(ctx);
    const walls = roomWallsOf(options.wallGroup);
    const others = walls ? [...unclaimed, walls] : unclaimed;
    const {rules = true, faces = true, corners = true} = options;
    const ruleSnap: Snap | null = rules && rule ? {...rule, rule: true} : null;
    const axis = options.axis ?? null;
    const body =
        options.body ??
        shiftBody(
            alignmentBodyOf(ctx.selected),
            target.clone().sub(product.position)
        );
    // Faces only on a free move: held to an axis, it lines up instead
    // (CO-5858).
    const face = faces && axis === null ? findFaceSnap(body, others) : null;
    const faceSnap: Snap | null = face && {
        position: target.clone().add(face.offset),
        distance: face.distance,
        route: 'face',
        rule: false,
        other: face.other,
        face: face.face,
        aligned: face.aligned,
    };
    // Corners only on a free move: held to one axis, a corner is a face
    // lined up across a gap.
    const corner =
        corners && axis === null && options.viewport
            ? findCornerSnap(
                  body,
                  others,
                  {camera: options.camera, viewport: options.viewport},
                  options.heldCorner ?? null
              )
            : null;
    const cornerSnap: Snap | null = corner && {
        position: target.clone().add(corner.offset),
        distance: corner.distance,
        route: 'corner',
        rule: false,
        other: corner.other,
        held: corner.held,
    };

    const order =
        face && withWall(face)
            ? [ruleSnap, faceSnap, cornerSnap]
            : [ruleSnap, cornerSnap, faceSnap];

    return order.filter((snap): snap is Snap => snap !== null);
};
